import { apiRequest } from './apiUtils';
import type {
  AcceptMarketplaceOrderResponseDto,
  ApplyToMarketplaceOpportunityRequestDto,
  ApplyToMarketplaceOpportunityResponseDto,
  CounterMarketplaceOrderRequestDto,
  CounterMarketplaceOrderResponseDto,
  CreateMarketplaceDisputeDto,
  DeclineMarketplaceOrderResponseDto,
  GetMarketplaceOrderResponseDto,
  ListMarketplaceOpportunitiesResponseDto,
  ListMarketplaceOrdersResponseDto,
  MarketplaceDispute,
  RequestMarketplaceInviteExtensionRequestDto,
  RequestMarketplaceInviteExtensionResponseDto,
} from '@foodfluence/shared';

export const lcApi = {
  listMarketplaceOrders: async (): Promise<ListMarketplaceOrdersResponseDto> => {
    return apiRequest({
      method: 'GET',
      endpoint: '/lc/marketplace-orders',
    });
  },

  getMarketplaceOrder: async (orderId: string): Promise<GetMarketplaceOrderResponseDto> => {
    return apiRequest({
      method: 'GET',
      endpoint: `/lc/marketplace-orders/${orderId}`,
    });
  },

  acceptMarketplaceOrder: async (orderId: string): Promise<AcceptMarketplaceOrderResponseDto> => {
    return apiRequest({
      method: 'POST',
      endpoint: `/lc/marketplace-orders/${orderId}/accept`,
    });
  },

  declineMarketplaceOrder: async (orderId: string): Promise<DeclineMarketplaceOrderResponseDto> => {
    return apiRequest({
      method: 'POST',
      endpoint: `/lc/marketplace-orders/${orderId}/decline`,
    });
  },

  reportDisputeForOrder: async (
    orderId: string,
    body: CreateMarketplaceDisputeDto,
  ): Promise<{ success: boolean; data: MarketplaceDispute & { id: string } }> => {
    return apiRequest({
      method: 'POST',
      endpoint: `/lc/marketplace-orders/${orderId}/dispute`,
      data: body,
    });
  },

  getLatestDisputeForOrder: async (
    orderId: string,
  ): Promise<{ success: boolean; data: (MarketplaceDispute & { id: string }) | null }> => {
    return apiRequest({
      method: 'GET',
      endpoint: `/lc/marketplace-orders/${orderId}/dispute`,
    });
  },

  requestInviteExtension: async (
    orderId: string,
    body?: RequestMarketplaceInviteExtensionRequestDto,
  ): Promise<RequestMarketplaceInviteExtensionResponseDto> => {
    return apiRequest({
      method: 'POST',
      endpoint: `/lc/marketplace-orders/${orderId}/request-invite-extension`,
      data: body ?? {},
    });
  },

  counterMarketplaceOrder: async (
    orderId: string,
    body: CounterMarketplaceOrderRequestDto,
  ): Promise<CounterMarketplaceOrderResponseDto> => {
    return apiRequest({
      method: 'POST',
      endpoint: `/lc/marketplace-orders/${orderId}/counter`,
      data: body,
    });
  },

  updateMarketplaceCounter: async (
    orderId: string,
    body: CounterMarketplaceOrderRequestDto,
  ): Promise<CounterMarketplaceOrderResponseDto> => {
    return apiRequest({
      method: 'PUT',
      endpoint: `/lc/marketplace-orders/${orderId}/counter`,
      data: body,
    });
  },

  generateContentUploadUrl: async (
    orderId: string,
    params: { slotId: string; fileName: string; contentType: string },
  ): Promise<{ url: string; filePath: string }> => {
    return apiRequest({
      method: 'POST',
      endpoint: `/lc/marketplace-orders/${orderId}/generate-content-upload-url`,
      data: params,
    });
  },

  submitContent: async (
    orderId: string,
    params: { slotId: string; files: Array<{ filePath: string; mimeType?: string }>; notesFromCreator?: string },
  ): Promise<{ success: boolean }> => {
    return apiRequest({
      method: 'POST',
      endpoint: `/lc/marketplace-orders/${orderId}/submit-content`,
      data: params,
    });
  },

  submitPostLink: async (
    orderId: string,
    params: { slotId: string; postUrl: string },
  ): Promise<{ success: boolean }> => {
    return apiRequest({
      method: 'POST',
      endpoint: `/lc/marketplace-orders/${orderId}/submit-post-link`,
      data: params,
    });
  },

  listOpportunities: async (): Promise<ListMarketplaceOpportunitiesResponseDto> => {
    return apiRequest({
      method: 'GET',
      endpoint: '/lc/opportunities',
    });
  },

  applyToOpportunity: async (
    opportunityId: string,
    body?: ApplyToMarketplaceOpportunityRequestDto,
  ): Promise<ApplyToMarketplaceOpportunityResponseDto> => {
    return apiRequest({
      method: 'POST',
      endpoint: `/lc/opportunities/${opportunityId}/apply`,
      data: body ?? {},
    });
  },
};

